/**
 * "Create channel": name (normalized and validated as you type), description, public/private, people to add
 * (who I may see per `can.seeDirectory`). Creating is optimistic: the channel is in the sidebar at once and opens;
 * online, a refusal (name taken by a channel I can't see …) comes back here within a moment and the draft stays.
 */
import type { ApiFailure } from "../ports.ts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isRestricted, capabilitiesFor } from "../logic/access.ts";
import { addableMembers, canCreateChannels, channelNameError, channelNameHint, knownChannelNames, normalizeChannelName, type MemberOption } from "../logic/channels.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** How long a create waits for the server before it counts as queued (the channel opens either way). */
export const CREATE_WAIT_MS = 4000;
const PEOPLE_LIMIT = 8;

export type CreateChannelState = {
  name: string;
  /** What the channel will be called. */
  slug: string;
  nameError?: MessageDescriptor;
  nameHint?: MessageDescriptor;
  /** Show `nameError` (once something was typed, or after a create attempt). */
  showNameError: boolean;
  purpose: string;
  isPrivate: boolean;
  /** People search. */
  query: string;
  /** Matching people not added yet. */
  people: MemberOption[];
  /** Added, in the order they were picked. */
  selected: MemberOption[];
  /** `can.createChannels`. */
  canCreate: boolean;
  /** Restricted creators need `can.invite` to add anyone. */
  canAddPeople: boolean;
  canSubmit: boolean;
  busy: boolean;
  /** The server refused the create. */
  error?: MessageDescriptor;
};

function initial(): CreateChannelState {
  return {
    name: "",
    slug: "",
    showNameError: false,
    purpose: "",
    isPrivate: false,
    query: "",
    people: [],
    selected: [],
    canCreate: false,
    canAddPeople: false,
    canSubmit: false,
    busy: false,
  };
}

export class CreateChannelViewModel extends ViewModel<CreateChannelState> {
  private readonly ws: WorkspaceViewModel;

  constructor(ws: WorkspaceViewModel) {
    super(initial());
    this.ws = ws;
    this.onDispose(ws.subscribe(() => this.derive({})));
    this.derive({});
  }

  /** Recomputes the derived fields after `patch`. */
  private derive(patch: Partial<CreateChannelState>): void {
    const s = { ...this.state, ...patch };
    const cache = this.ws.state.cache;
    const now = this.ws.deps.clock.now();
    const next: CreateChannelState = { ...s, slug: normalizeChannelName(s.name) };
    const error = cache === undefined ? undefined : channelNameError(s.name, knownChannelNames(cache, undefined));
    if (error === undefined) delete next.nameError;
    else next.nameError = error;
    const hint = channelNameHint(s.name);
    if (hint === undefined) delete next.nameHint;
    else next.nameHint = hint;
    const me = cache?.members[cache.meId];
    next.canCreate = cache !== undefined && canCreateChannels(cache, now);
    next.canAddPeople = me !== undefined && (!isRestricted(me) || capabilitiesFor(me, now).invite);
    next.people = cache === undefined || !next.canAddPeople ? [] : addableMembers(cache, s.query, s.selected.map((p) => p.userId), now, PEOPLE_LIMIT);
    next.canSubmit = next.canCreate && error === undefined && !s.busy;
    this.setState(next);
  }

  setName(name: string): void {
    this.derive({ name, showNameError: name.trim() !== "", error: undefined });
  }

  setPurpose(purpose: string): void {
    this.derive({ purpose });
  }

  setPrivate(isPrivate: boolean): void {
    this.derive({ isPrivate });
  }

  setQuery(query: string): void {
    this.derive({ query });
  }

  addMember(userId: string): void {
    const p = this.state.people.find((x) => x.userId === userId);
    if (p === undefined) return;
    this.derive({ selected: [...this.state.selected, p], query: "" });
  }

  removeMember(userId: string): void {
    this.derive({ selected: this.state.selected.filter((x) => x.userId !== userId) });
  }

  /** Back to an empty form (each time the sheet opens). */
  reset(): void {
    this.setState(initial());
    this.derive({});
  }

  /**
   * Creates the channel. Resolves with its id once it may be opened (confirmed, or queued offline / slow network), or
   * undefined when the form isn't valid or the server refused it (`error` says why).
   */
  async submit(): Promise<string | undefined> {
    const s = this.state;
    if (!s.canSubmit) {
      this.derive({ showNameError: true });
      return undefined;
    }
    const id = this.ws.deps.ids.next();
    const req: { kind: "public" | "private"; name: string; id: string; purpose?: string; memberIds?: string[] } = {
      kind: s.isPrivate ? "private" : "public",
      name: s.slug,
      id,
    };
    if (s.purpose.trim() !== "") req.purpose = s.purpose.trim();
    if (s.selected.length > 0) req.memberIds = s.selected.map((p) => p.userId);
    const created = this.ws.deps.data.createChannelQueued(this.ws.state.wsId, req);
    if (!this.ws.state.network) {
      created.catch(() => {});
      return id;
    }
    this.derive({ busy: true, error: undefined });
    let cancel: (() => void) | undefined;
    const timeout = new Promise<"queued">((resolve) => {
      cancel = this.ws.deps.clock.setTimer(CREATE_WAIT_MS, () => resolve("queued"));
    });
    try {
      await Promise.race([created, timeout]);
      created.catch(() => {});
      this.derive({ busy: false });
      return id;
    } catch (e) {
      const code = (e as Partial<ApiFailure> | undefined)?.code;
      this.derive({
        busy: false,
        showNameError: true,
        error: code === "name_taken" ? msg("channels.name.taken", { name: s.slug }) : code === "forbidden" ? msg("channels.create.forbidden") : msg("common.error"),
      });
      return undefined;
    } finally {
      cancel?.();
    }
  }
}
